import moment from "moment";
import { Fragment, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { FiCheck, FiEye } from "react-icons/fi";
import NumberFormat from "react-number-format";
import ReactPaginate from "react-paginate";
import { useDispatch } from "react-redux";
import swal from "@sweetalert/with-react";
import Card from "../../components/Card";
import Loader from "../../components/Loader";
import Modal from "../../components/Modal";
import Table, { Td, Th } from "../../components/Table";
import { setInterface } from "../../redux/slices/interfaceSlice";
import service from "../../service";
import BigLoader from "../../components/BigLoader";

export default function Order() {
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingPost, setLoadingPost] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detail, setDetail] = useState([]);
  const [page, setPage] = useState(1);
  const _modalDetail = useRef();

  const getDetail = (id) => {
    setLoadingDetail(true);
    service
      .get("/api/checkout/" + id)
      .then((response) => {
        setLoadingDetail(false);
        const { items } = response.data;
        setDetail(items);
      })
      .catch((e) => {
        setLoadingDetail(false);
      });
  };

  const getData = () => {
    setLoading(true);
    service
      .get("/api/checkout", { params: { page } })
      .then((response) => {
        const { total, data } = response.data;
        setTotal(total);
        setData(data);
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
      });
  };

  const confirmPayment = (id) => {
    swal({
      title: "Konfirmasi pembayaran?",
      text: "Order akan ditandai LUNAS dan akses digital diaktifkan (QRIS manual).",
      icon: "warning",
      buttons: ["Batal", "Ya, aktifkan"],
    }).then((ok) => {
      if (!ok) return;
      setLoadingPost(true);
      service
        .post("/api/checkout/confirm/" + id)
        .then((res) => {
          setLoadingPost(false);
          getData();
          swal(
            "Berhasil",
            res.data?.message || "Pembayaran dikonfirmasi & akses aktif",
            "success"
          );
        })
        .catch((e) => {
          setLoadingPost(false);
          swal(
            "Gagal",
            e?.response?.data?.message || "Gagal konfirmasi pembayaran",
            "error"
          );
        });
    });
  };

  useEffect(() => {
    dispatch(setInterface({ active: "Pesanan", pageTitle: "Pesanan" }));
  }, [dispatch]);

  useEffect(() => {
    getData();
  }, [page]);

  return (
    <Fragment>
      <Helmet>
        <title>Pesanan</title>
      </Helmet>
      <Card>
        {loading ? (
          <div className="h-64 flex justify-center items-center flex-col">
            <Loader className="bg-black-100" />
            <span className="mt-3 text-black-300 text-sm">Memuat</span>
          </div>
        ) : data.length ? (
          <Fragment>
            <Table>
              <thead>
                <tr>
                  <Th>TANGGAL</Th>
                  <Th>NAMA USER</Th>
                  <Th>TOTAL</Th>
                  <Th>STATUS</Th>
                  <Th>AKSI</Th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => {
                  let statusColor = item.is_paid
                    ? "text-yellow-300"
                    : "text-red-300";
                  let status = item.is_paid ? "Diterima" : "Belum Dibayar";
                  if (item.status === 2) {
                    statusColor = "text-primary-500";
                    status = "Diproses";
                  } else if (item.status === 3) {
                    statusColor = "text-green-300";
                    status = "Terkirim";
                  }
                  return (
                    <tr key={`list-${index}`}>
                      <Td className="text-center">
                        {moment(item.created_at).format("HH:mm DD/MM/YYYY")}
                      </Td>
                      <Td>{item.user?.fullname}</Td>
                      <Td className="text-right">
                        <NumberFormat
                          prefix="Rp"
                          thousandSeparator={true}
                          value={item.total}
                          displayType="text"
                        />
                      </Td>
                      <Td className="text-center">
                        <span className={statusColor}>{status}</span>
                      </Td>
                      <Td>
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => {
                              getDetail(item.id);
                              _modalDetail.current.show();
                            }}
                            type="button"
                            className="p-1.5 rounded-sm border border-white-300 text-sm transition-colors hover:bg-primary-500 hover:border-primary-600 hover:text-white-100 flex items-center"
                          >
                            <FiEye />
                          </button>
                          {!item.is_paid && (
                            <button
                              onClick={() => confirmPayment(item.id)}
                              type="button"
                              title="Konfirmasi bayar & aktifkan akses"
                              className="p-1.5 rounded-sm border border-white-300 text-sm transition-colors hover:bg-green-200 hover:border-green-300 hover:text-white-100 flex items-center"
                            >
                              <FiCheck />
                            </button>
                          )}
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            <ReactPaginate
              pageCount={total}
              pageRangeDisplayed={2}
              marginPagesDisplayed={2}
              initialPage={page - 1}
              containerClassName="flex flex-wrap mt-6 gap-1"
              previousLinkClassName="border border-white-300 rounded-sm py-1.5 px-3 text-sm transition-colors hover:bg-primary-500 hover:border-primary-600 hover:text-white-100 block"
              nextLinkClassName="border border-white-300 rounded-sm py-1.5 px-3 text-sm transition-colors hover:bg-primary-500 hover:border-primary-600 hover:text-white-100 block"
              pageLinkClassName="border border-white-300 rounded-sm py-1.5 px-3 text-sm transition-colors hover:bg-primary-500 hover:border-primary-600 hover:text-white-100 block"
              activeLinkClassName="bg-primary-500 border-primary-600 text-white-100"
              onPageChange={({ selected }) => setPage(selected + 1)}
            />
          </Fragment>
        ) : (
          <div className="text-center flex flex-col justify-center items-center h-40 text-black-300">
            <p>Belum ada konten</p>
          </div>
        )}
      </Card>
      <Modal title="Detail" ref={_modalDetail}>
        {loadingDetail ? (
          <div className="h-64 flex justify-center items-center flex-col">
            <Loader className="bg-black-100" />
            <span className="mt-3 text-black-300 text-sm">Memuat</span>
          </div>
        ) : (
          <Fragment>
            <Table>
              <thead>
                <tr>
                  <Th>NO</Th>
                  <Th>PRODUK</Th>
                  <Th>HARGA</Th>
                  <Th>QTY</Th>
                  <Th>TOTAL</Th>
                </tr>
              </thead>
              <tbody>
                {detail.map((item, index) => (
                  <tr key={`key-${index}`}>
                    <Td>{index + 1}</Td>
                    <Td>{item.title}</Td>
                    <Td>
                      <NumberFormat
                        prefix="Rp"
                        thousandSeparator={true}
                        value={item.price}
                        displayType="text"
                      />
                    </Td>
                    <Td>{item.qty}</Td>
                    <Td>
                      <NumberFormat
                        prefix="Rp"
                        thousandSeparator={true}
                        value={item.qty * item.price}
                        displayType="text"
                      />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Fragment>
        )}
      </Modal>
      {loadingPost && <BigLoader />}
    </Fragment>
  );
}
